import { useEffect, useRef, useState } from "react";

import {
  markMobileLifecycleBackground,
  markMobileLifecycleForeground,
  mobileLifecycleSnapshot,
  onMobileLifecycle,
  setMobileReachability,
  type MobileConnectionState,
} from "~/lib/platform";

export function useMobileConnection(unreadBadgeCount: number): MobileConnectionState {
  const [connection, setConnection] = useState<MobileConnectionState>("online");
  const unreadBadgeCountRef = useRef(0);

  useEffect(() => {
    unreadBadgeCountRef.current = unreadBadgeCount;
  }, [unreadBadgeCount]);

  useEffect(() => {
    let cancelled = false;
    void mobileLifecycleSnapshot().then((snapshot) => {
      if (!cancelled && snapshot) setConnection(snapshot.connection);
    });
    let unlisten: (() => void) | null = null;
    void onMobileLifecycle((snapshot) => setConnection(snapshot.connection)).then((unlistenFn) => {
      if (cancelled) unlistenFn();
      else unlisten = unlistenFn;
    });
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);

  useEffect(() => {
    const applyNative = (snapshot: Awaited<ReturnType<typeof markMobileLifecycleForeground>>) => {
      if (snapshot) setConnection(snapshot.connection);
    };
    const reconcile = (online: boolean) => {
      void setMobileReachability(online).then((snapshot) => {
        setConnection(snapshot?.connection ?? (online ? "online" : "offline"));
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        void markMobileLifecycleBackground(unreadBadgeCountRef.current).then(applyNative);
      } else {
        void markMobileLifecycleForeground().then(applyNative);
      }
    };
    const handleOnline = () => reconcile(true);
    const handleOffline = () => reconcile(false);

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    if (typeof navigator !== "undefined" && navigator.onLine === false) handleOffline();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return connection;
}
